import type { Currency, Entry, EntryType } from '@/types/domain'

export type MonthlyKey = 'BRL' | Currency

export interface MonthlyRow {
  key: MonthlyKey
  months: number[]
  total: number
}

export interface MonthlySection {
  tipo: EntryType
  rows: MonthlyRow[]
  monthTotalsBRL: number[]
  yearTotalBRL: number
}

export interface MonthlyMatrix {
  rendimentos: MonthlySection
  impostos: MonthlySection
}

const CURRENCY_ORDER: Currency[] = ['USD', 'CAD']

function newRow(key: MonthlyKey): MonthlyRow {
  return { key, months: Array<number>(12).fill(0), total: 0 }
}

function emptySection(tipo: EntryType): MonthlySection {
  return {
    tipo,
    rows: [
      newRow('BRL'),
      ...CURRENCY_ORDER.map((c) => newRow(c)),
    ],
    monthTotalsBRL: Array<number>(12).fill(0),
    yearTotalBRL: 0,
  }
}

function round(value: number, decimals: number): number {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

/**
 * Aggregates entries into a per-month, per-currency matrix split by tipo.
 * Each section has one row per currency (BRL first, then USD/CAD) and 12
 * month buckets. The BRL row is the converted total (sum of valorBRL); the
 * USD/CAD rows are the sum of valorOriginal in that currency. Entries
 * whose snapshot is not 'calculado' contribute their original-currency
 * totals only (BRL stays at 0).
 */
export function computeMonthlyMatrix(entries: Entry[]): MonthlyMatrix {
  const matrix: MonthlyMatrix = {
    rendimentos: emptySection('rendimento'),
    impostos: emptySection('imposto_pago_exterior'),
  }

  for (const entry of entries) {
    const month = Number.parseInt(entry.dataRecebimento.slice(5, 7), 10) - 1
    if (month < 0 || month > 11) continue
    const section = entry.tipo === 'rendimento' ? matrix.rendimentos : matrix.impostos

    const origRow = section.rows.find((r) => r.key === entry.moedaOrigem)
    if (origRow) {
      const prev = origRow.months[month] ?? 0
      origRow.months[month] = round(prev + entry.valorOriginal, 4)
    }

    if (entry.snapshot.status === 'calculado' && typeof entry.snapshot.valorBRL === 'number') {
      const brlRow = section.rows.find((r) => r.key === 'BRL')
      if (brlRow) {
        const prev = brlRow.months[month] ?? 0
        brlRow.months[month] = round(prev + entry.snapshot.valorBRL, 2)
      }
      const prevTotal = section.monthTotalsBRL[month] ?? 0
      section.monthTotalsBRL[month] = round(prevTotal + entry.snapshot.valorBRL, 2)
    }
  }

  for (const section of [matrix.rendimentos, matrix.impostos]) {
    for (const row of section.rows) {
      row.total = round(
        row.months.reduce((acc, n) => acc + n, 0),
        row.key === 'BRL' ? 2 : 4,
      )
    }
    section.yearTotalBRL = round(
      section.monthTotalsBRL.reduce((acc, n) => acc + n, 0),
      2,
    )
  }

  return matrix
}

export const MONTH_LABELS_PT_SHORT = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const
